
import React from "react";
import { useAuth } from "@/contexts/auth-context";
import { AgentFormData } from "@/models/agent-form";
import AccessDenied from "@/components/agent-upload/AccessDenied";
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { agentFormSchema } from "@/models/agent-form";
import AgentFormContent from "./AgentFormContent";
import AgentFormSubmitHandler from "./AgentFormSubmitHandler";
import FormError from "./FormError";
import { useDbConnectivity } from "@/hooks/use-db-connectivity";

const AgentFormContainer: React.FC = () => {
  const { user } = useAuth();
  const { error: dbError } = useDbConnectivity();
  
  const form = useForm<AgentFormData>({
    resolver: zodResolver(agentFormSchema),
    defaultValues: {
      name: "",
      description: "",
      category: "",
      tags: "",
      pricingModel: "free",
      fullDescription: "",
      features: "",
      useCases: "",
      requirements: "",
      version: "1.0.0",
      file: null,
      isPublic: true,
      customCode: "",
      promptTemplate: ""
    }
  });
  
  // Redirect if user is not logged in or not a creator
  if (!user || user.profileType !== "creator") {
    return <AccessDenied isLoggedIn={!!user} isCreator={user?.profileType === "creator"} />;
  }
  
  // This function exists so the actual submission can be handled in the AgentFormSubmitHandler
  const onSubmit: SubmitHandler<AgentFormData> = (data) => {
    console.log("Form submitted:", data);
    // This is intentionally left empty as the actual handling is done in AgentFormSubmitHandler
  };
  
  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 max-w-3xl mx-auto p-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold">Create an Agent</h1>
          <p className="text-muted-foreground">
            Share your AI agent with the community and start earning.
          </p>
        </div>
        
        <FormError error={dbError} />
        
        <AgentFormContent />
        
        <AgentFormSubmitHandler 
          onSubmit={handleSubmit => form.handleSubmit(handleSubmit)}
          formState={form.formState}
        />
      </form>
    </FormProvider>
  );
};

export default AgentFormContainer;
